import React, { useMemo, useState } from "react";

import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";

import concat from "lodash/concat";
import omit from "lodash/omit";

import FilterDropdown, {
  FilterDropDownButton
} from "components/filter-dropdown";
import useLockedFilterValues, {
  type LockedFilterValues
} from "features/reports/hooks/use-locked-filter-values.hook";

import FilterAutocomplete from "./filter-autocomplete.component";
import Filter from "./filter.component";

const isFilterValueEmpty = (value: any) =>
  value == null || (Array.isArray(value) && !value.length);

/**
 * What the bar takes from its `selectedFilters` and the permission lock (ENG-1595) each time
 * either one changes: the keys to show as chips, and the locked values to seed.
 */
function getFilterBarSync(
  selectedFilters: Record<string, any> | undefined,
  lockedValues: LockedFilterValues,
  seededLockSignature: string
) {
  const filters = selectedFilters || {};
  const shownKeys = [
    ...Object.keys(filters).filter((key) => filters[key] !== null),
    ...Array.from(lockedValues.lockedKeys)
  ];

  // Only seed the locked value when the lock itself is new — on mount once it resolves, again if
  // the user moves to a report with a different lock, and again when the option labels arrive
  // (params carry the ids before the labels' fetch resolves, so the signature must cover both).
  // Re-applying it on every render would overwrite the selection a clinic-scoped user just made
  // within their allowed clinics, since choosing one changes the `selectedFilters` identity and
  // re-runs the merge.
  const lockSignature = JSON.stringify([
    lockedValues.params,
    lockedValues.options
  ]);
  const isNewLock = lockSignature !== seededLockSignature;

  // Seeding fills an empty filter; it never widens one that already carries a narrower choice
  // (a saved view, or a selection made before the lock resolved).
  const seeds = isNewLock
    ? Object.fromEntries(
        Object.entries(lockedValues.options).filter(([key]) =>
          isFilterValueEmpty(filters[key])
        )
      )
    : {};

  return { shownKeys, lockSignature, isNewLock, seeds };
}

export type CustomFilterOption = {
  label: string;
  filterKey: string;
  renderViewMode?: (filterKey: string, metadata: any) => React.ReactNode;
  editComponent?: (
    filterKey: string,
    setMetadata: (metadata: any) => void
  ) => React.ReactNode;
  onEditTriggered?: (filterKey: string) => void;
  onApplyEdit?: (filterKey: string) => void;
  onCancelEdit?: (filterKey: string) => void;
  onDelete?: (filterKey: string) => void;
  /**
   * Rendered right after the chip, in every state (empty button, value chip, edit mode). Reports
   * use it for a help icon that explains the filter; it is not part of the chip's click target.
   */
  helpAdornment?: React.ReactNode;
  /**
   * Shows the filter's button disabled, with this as its tooltip. For a tab whose own rule makes
   * the filter meaningless (the Fax Center's Unassigned queue is always inbound, with no patient).
   * The button keeps its label and width, so the bar does not reflow, and whatever the user had
   * picked is kept and comes back once the filter is enabled again.
   */
  disabledReason?: string;
};

type CustomFilterContainerProps = {
  tooltipText?: string;
  moreFiltersLabel?: string;
  filters: CustomFilterOption[];
  mostCommonFilters?: CustomFilterOption[];
  selectedFilters?: Record<string, any>;
  wrap?: boolean;
  /**
   * Keeps the chips in a wrapping row at every width. By default the bar turns into a column at
   * 1512px and below, one chip per line; `wrap` does not change that (it only adds wrapping and a
   * top margin above 1512px).
   */
  keepInRow?: boolean;
};

export default function CustomFiltersList({
  tooltipText = "Click to add a custom filter",
  moreFiltersLabel = "More Filters",
  filters,
  mostCommonFilters,
  selectedFilters,
  wrap,
  keepInRow = false
}: CustomFilterContainerProps) {
  const isSmallScreen = useMediaQuery("(max-width:1512px)") && !keepInRow;
  const [optionInEditMode, setOptionInEditMode] = useState<
    string | null | undefined
  >();
  const [selectedOptionValues, setSelectedOptionValues] = useState<string[]>(
    []
  );
  const [selectedFiltersMeta, setSelectedFiltersMeta] = useState<
    Record<string, any>
  >({});

  // ENG-1595: a permission-locked filter has a value from the moment the bar mounts, but most
  // callers never pass `selectedFilters`, so without this it would render as an untouched dropdown
  // button and the lock would be invisible.
  const lockedValues = useLockedFilterValues();
  const [seededLockSignature, setSeededLockSignature] = useState("");

  // Merged while rendering rather than in an effect, so the first paint already shows the chips
  // instead of an empty bar that fills in a frame later. `syncedInputs` records the inputs last
  // merged; the block runs again only when either one changes, exactly as the effect did.
  const [syncedInputs, setSyncedInputs] = useState<{
    selectedFilters?: Record<string, any>;
    lockedValues: LockedFilterValues;
  } | null>(null);
  if (
    syncedInputs === null ||
    syncedInputs.selectedFilters !== selectedFilters ||
    syncedInputs.lockedValues !== lockedValues
  ) {
    setSyncedInputs({ selectedFilters, lockedValues });
    const { shownKeys, lockSignature, isNewLock, seeds } = getFilterBarSync(
      selectedFilters,
      lockedValues,
      seededLockSignature
    );
    // Deduplicate to prevent exponential array growth on re-renders
    setSelectedOptionValues((prev) =>
      Array.from(new Set([...prev, ...shownKeys]))
    );
    if (isNewLock) {
      setSeededLockSignature(lockSignature);
    }
    setSelectedFiltersMeta((prev) => ({
      ...prev,
      ...selectedFilters,
      ...seeds
    }));
  }

  const [selectedOptions, availableOptions] = useMemo(() => {
    const selectedKeys = new Set(selectedOptionValues);
    return [
      filters.filter((option) => selectedKeys.has(option.filterKey)),
      filters
        .filter((option) => !selectedKeys.has(option.filterKey))
        .map(({ label, filterKey }) => ({
          label,
          filterKey
        }))
    ];
  }, [selectedOptionValues, filters]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleIconButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (
    option: { label: string; filterKey: string } | null
  ) => {
    setSelectedOptionValues(
      concat(selectedOptionValues, String(option?.filterKey))
    );
    setOptionInEditMode(option?.filterKey);
  };

  const handleClearMetadata = (value: string) => {
    setSelectedFiltersMeta(omit(selectedFiltersMeta, value));
    setSelectedOptionValues([
      ...selectedOptionValues.filter((optionValue) => optionValue !== value)
    ]);
  };

  const handleFinishEdit = (value: string) => {
    setOptionInEditMode("");
  };

  const handleSetFilterData = (value: string, data: any) => {
    if (data === null || data === undefined) {
      handleClearMetadata(value);
    } else {
      setSelectedFiltersMeta({
        ...selectedFiltersMeta,
        [value]: data
      });
    }
  };

  const handleStartEdit = (value: string) => {
    setOptionInEditMode(value);
  };

  return (
    <Stack
      direction={isSmallScreen ? "column" : "row"}
      rowGap={1}
      columnGap={2}
      data-testid="filters-bar"
      alignItems={isSmallScreen ? "flex-start" : "center"}
      justifyContent="flex-start"
      sx={{
        mr: 1,
        mt: wrap ? 3 : undefined,
        flexWrap: wrap || keepInRow ? "wrap" : undefined
      }}
    >
      {mostCommonFilters?.map((filter) => {
        if (filter.disabledReason) {
          return (
            <Tooltip key={filter.filterKey} title={filter.disabledReason}>
              {/* A disabled button fires no pointer events, so the tooltip needs a wrapper. */}
              <span>
                <FilterDropDownButton
                  label={filter.label}
                  handleClick={() => undefined}
                  disabled
                />
              </span>
            </Tooltip>
          );
        }
        const dropdown = (
          <FilterDropdown
            key={filter.filterKey}
            option={filter}
            isEdit={optionInEditMode === filter.filterKey}
            handleSetFilterData={handleSetFilterData}
            selectedFiltersMeta={selectedFiltersMeta}
            metadata={selectedFiltersMeta[filter.filterKey]}
            handleClearMetadata={handleClearMetadata}
            onStartEdit={handleStartEdit}
            onFinishEdit={handleFinishEdit}
            isLocked={lockedValues.lockedKeys.has(filter.filterKey)}
            isPinned={lockedValues.pinnedKeys.has(filter.filterKey)}
          />
        );
        if (!filter.helpAdornment) return dropdown;
        return (
          <Stack
            key={filter.filterKey}
            direction="row"
            alignItems="center"
            spacing={0.5}
          >
            {dropdown}
            {filter.helpAdornment}
          </Stack>
        );
      })}
      {Boolean(selectedOptionValues.length) && (
        <Stack
          spacing={2}
          direction="row"
          alignItems="center"
          flexWrap={keepInRow ? "wrap" : undefined}
          useFlexGap={keepInRow}
          data-testid="filters-bar-selected"
        >
          {selectedOptions.map((option) => (
            <Filter
              key={option.filterKey}
              option={option}
              metadata={selectedFiltersMeta[option.filterKey]}
              isEdit={optionInEditMode === option.filterKey}
              onStartEdit={handleStartEdit}
              onFinishEdit={handleFinishEdit}
              handleClearMetadata={handleClearMetadata}
              handleSetFilterData={handleSetFilterData}
              isLocked={lockedValues.lockedKeys.has(option.filterKey)}
              isPinned={lockedValues.pinnedKeys.has(option.filterKey)}
            />
          ))}
        </Stack>
      )}
      {Boolean(availableOptions.length) && (
        <Tooltip title={tooltipText}>
          <span>
            <FilterDropDownButton
              label={moreFiltersLabel}
              handleClick={handleIconButtonClick}
            />{" "}
          </span>
        </Tooltip>
      )}
      <FilterAutocomplete
        options={availableOptions}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        onSelect={handleSelect}
      />
    </Stack>
  );
}
